import { defineStore } from 'pinia';

const database = 'CachedImagesStore';
const table = 'Images';

let databasePromise: Promise<IDBDatabase> | null = null;
let resetPromise: Promise<void> | null = null;

function openDatabase() {
  if (databasePromise) {
    return databasePromise;
  }

  const request = indexedDB.open(database, 1);

  request.onupgradeneeded = () => {
    const db = request.result;

    if (!db.objectStoreNames.contains(table)) {
      db.createObjectStore(table);
    }
  };

  databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onblocked = () => {
      console.warn('[Store] IndexedDB open blocked for cached images store');
    };
    request.onerror = (event) => {
      databasePromise = null;
      console.error('[Store] Error opening IndexedDB:', request.error ?? event);
      reject(request.error ?? event);
    };
  });

  return databasePromise.then((db) => {
    db.onversionchange = () => {
      db.close();
      databasePromise = null;
    };

    return db;
  });
}

function waitForTransaction(transaction: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = (event) => reject(transaction.error ?? event);
    transaction.onabort = (event) => reject(transaction.error ?? event);
  });
}

async function clearObjectStore() {
  const db = await openDatabase();

  const transaction = db.transaction(table, 'readwrite');
  transaction.objectStore(table).clear();
  await waitForTransaction(transaction);
}

export async function resetCachedImagesStore() {
  if (!resetPromise) {
    resetPromise = clearObjectStore().finally(() => {
      resetPromise = null;
    });
  }

  await resetPromise;
}

export const useCachedImagesStore = defineStore('cachedImages', {
  actions: {
    async set(key: string, blob: Blob) {
      try {
        const db = await openDatabase();
        const transaction = db.transaction(table, 'readwrite');
        const store = transaction.objectStore(table);
        const putRequest = store.put(blob, key);

        await new Promise<void>((resolve, reject) => {
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = (err) => {
            console.error('[Store] Error storing in IndexedDB:', key, err);
            reject(err);
          };
        });

        await waitForTransaction(transaction);
      } catch (error) {
        console.warn('[Store] Skipping IndexedDB cache write:', key, error);
      }
    },

    async get(key: string) {
      // Validate key
      if (!key || key.trim() === '') {
        console.warn('[Store] Invalid key provided to get():', key);
        return undefined;
      }

      try {
        const db = await openDatabase();
        const transaction = db.transaction(table, 'readonly');
        const store = transaction.objectStore(table);
        const getRequest = store.get(key);

        const result = await new Promise<Blob | undefined>((resolve, reject) => {
          getRequest.onsuccess = () => {
            resolve(getRequest.result);
          };

          getRequest.onerror = (event) => {
            console.error('[Store] Error retrieving from IndexedDB:', key, event);
            reject(getRequest.error ?? event);
          };
        });

        await waitForTransaction(transaction);
        return result;
      } catch (error) {
        console.warn('[Store] IndexedDB cache read unavailable:', key, error);
        return undefined;
      }
    },

    async findKeyBySuffix(suffix: string) {
      if (!suffix || suffix.trim() === '') {
        return undefined;
      }

      try {
        const db = await openDatabase();
        const transaction = db.transaction(table, 'readonly');
        const store = transaction.objectStore(table);
        const normalizedSuffix = suffix.replace(/\\/g, '/').toLowerCase();
        const matchedKey = await new Promise<string | undefined>((resolve, reject) => {
          let match: string | undefined;
          const cursorRequest = store.openKeyCursor();

          cursorRequest.onsuccess = () => {
            const cursor = cursorRequest.result;
            if (!cursor) {
              resolve(match);
              return;
            }

            const key = cursor.primaryKey;
            if (typeof key === 'string' && key.replace(/\\/g, '/').toLowerCase().endsWith(normalizedSuffix)) {
              if (match && match !== key) {
                resolve(undefined);
                return;
              }

              match = key;
            }

            cursor.continue();
          };

          cursorRequest.onerror = (event) => reject(cursorRequest.error ?? event);
        });

        return matchedKey;
      } catch (error) {
        console.warn('[Store] IndexedDB suffix lookup unavailable:', suffix, error);
        return undefined;
      }
    },

    async clear() {
      await clearObjectStore();
    },
  },
});
